import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { useBase } from "@/context/BaseContext";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { AuthImage } from "@/components/AuthImage";
import { PeriodFilter } from "@/components/PeriodFilter";
import { PeriodSelector } from "@/components/PeriodSelector";
import { fmtDate, inRange } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Check, CheckCheck, X, Images, ClipboardCheck, CalendarClock } from "lucide-react";

const CATEGORIES = [
  { key: "ciclista", label: "Ciclistas" },
  { key: "motoboy", label: "Motoboys" },
  { key: "motociclista", label: "Motoboys" },
  { key: "administrativo", label: "Administrativo" },
  { key: "gestor", label: "Gestão" },
];
const today = () => new Date().toISOString().slice(0, 10);
const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

function ManualPunch() {
  const { data, loading, error, refetch } = useFetch("/admin/punch-targets");
  const punches = useFetch("/admin/admin-punches");
  const { selectedBaseId } = useBase();
  const collabs = useFetch("/admin/collaborators");
  const [date, setDate] = useState(today());
  const [times, setTimes] = useState({});
  const [types, setTypes] = useState({});
  const [busy, setBusy] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  // Com base selecionada, /admin/collaborators já vem filtrado pela base (base_id injetado); punch-targets não.
  const inBaseIds = new Set((collabs.data?.collaborators || []).map((c) => c.id));
  const targets = (data?.targets || []).filter((t) => !selectedBaseId || inBaseIds.has(t.id) || t.role === "gestor");
  const groups = {};
  targets.forEach((t) => { const c = t.category || t.role || "outros"; (groups[c] = groups[c] || []).push(t); });
  const orderedCats = [...new Set([...CATEGORIES.map((c) => c.key), ...Object.keys(groups)])].filter((c) => groups[c]);
  const catLabel = (c) => (CATEGORIES.find((x) => x.key === c)?.label) || c;
  const recent = (punches.data?.punches || []).filter((pu) => inRange(pu.date || pu.timestamp, start, end));

  const register = async (person) => {
    const time = times[person.id];
    const type = types[person.id];
    if (!time) { toast.error("Informe o horário"); return; }
    if (!type) { toast.error("Selecione Entrada ou Saída"); return; }
    setBusy(person.id);
    try {
      await api.post("/admin/manual-punch", { person_id: person.id, type, date, time });
      toast.success(`Ponto (${type === "entrada" ? "Entrada" : "Saída"}) registrado para ${person.name.split(" ")[0]}`);
      setTypes((s) => ({ ...s, [person.id]: "" }));
      await punches.refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4">
        <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-orange-400" /><Label className="text-slate-300">Data do registro</Label></div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ponto-date" className="w-44 border-slate-700 bg-slate-950 text-slate-100" />
        <span className="text-xs text-slate-500">Digite o horário, selecione Entrada ou Saída e salve o ponto de cada colaborador.</span>
      </div>

      {orderedCats.map((cat) => (
        <div key={cat} className="rounded-xl border border-slate-800 bg-slate-900">
          <div className="border-b border-slate-800 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-orange-300">{catLabel(cat)} • {groups[cat].length}</div>
          <div className="divide-y divide-slate-800">
            {groups[cat].map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`ponto-person-${p.id}`}>
                <span className="min-w-0 flex-1 truncate font-medium text-slate-100">{p.name}</span>
                <Input type="time" value={times[p.id] || ""} onChange={(e) => setTimes((s) => ({ ...s, [p.id]: e.target.value }))} data-testid={`ponto-time-${p.id}`} className="w-32 border-slate-700 bg-slate-950 text-slate-100" />
                <Select value={types[p.id] || ""} onValueChange={(v) => setTypes((s) => ({ ...s, [p.id]: v }))}>
                  <SelectTrigger data-testid={`ponto-type-${p.id}`} className="w-36 border-slate-700 bg-slate-950 text-slate-100"><SelectValue placeholder="Tipo *" /></SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                    <SelectItem value="entrada">Entrada</SelectItem>
                    <SelectItem value="saida">Saída</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-8 bg-orange-500 text-white hover:bg-orange-600" disabled={busy === p.id || !times[p.id] || !types[p.id]} onClick={() => register(p)} data-testid={`ponto-save-${p.id}`}>Salvar</Button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="rounded-xl border border-slate-800 bg-slate-900">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 px-4 py-3">
          <Clock className="h-4 w-4 text-sky-400" />
          <h3 className="text-base font-medium text-slate-200">Registros recentes</h3>
          <div className="ml-auto"><PeriodFilter start={start} end={end} setStart={setStart} setEnd={setEnd} testPrefix="ponto-reg-period" label="Filtrar" /></div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-800">
              {recent.slice(0, 60).map((pu) => (
                <tr key={pu.id} className="hover:bg-slate-800/40">
                  <td className="px-4 py-2.5 font-medium text-slate-100">{pu.funcionario_name}</td>
                  <td className="px-4 py-2.5"><span className={`rounded px-2 py-0.5 text-xs font-semibold ${pu.type === "entrada" ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>{pu.type}</span></td>
                  <td className="px-4 py-2.5 text-slate-400">{fmtDate(pu.date)} · {timeOf(pu.timestamp)}</td>
                  <td className="px-4 py-2.5 text-slate-500">{pu.base_name || "—"}</td>
                </tr>
              ))}
              {recent.length === 0 && <tr><td colSpan={4}><Empty label="Sem registros no período" /></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PunchAudit() {
  const { data, loading, error, refetch } = useFetch("/admin/punch-audit");
  const { selectedBaseId } = useBase();
  const collabs = useFetch("/admin/collaborators");
  const [busy, setBusy] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  // Base selecionada: só colaboradores vinculados àquela base (mesmo critério do Dashboard: base_ids do colaborador).
  const basesOf = {};
  (collabs.data?.collaborators || []).forEach((c) => { basesOf[c.id] = c.base_ids || []; });
  const inBase = (it) => !selectedBaseId || !basesOf[it.person_id] || basesOf[it.person_id].includes(selectedBaseId);
  const items = (data?.items || []).filter((it) => inBase(it) && inRange(it.date || it.timestamp, start, end));
  const visibleIds = items.map((it) => it.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const pendingVisible = items.filter((it) => it.review_status !== "approved" && it.review_status !== "reproved");
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected(allVisibleSelected ? new Set() : new Set(visibleIds));

  const bulkApprove = async (ids) => {
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/punch-audit/approve-bulk", { ids });
      const n = r.data?.approved ?? r.data?.count ?? r.data?.updated ?? ids.length;
      toast.success(`${n} ponto(s) aprovado(s)`);
      setSelected(new Set());
      await refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };
  const approveSelected = () => bulkApprove([...selected]);
  const approveAllPending = () => {
    const ids = pendingVisible.map((it) => it.id);
    if (!ids.length) { toast.info("Nenhum ponto pendente na lista atual"); return; }
    if (window.confirm(`Aprovar todos os ${ids.length} pontos pendentes da lista atual?`)) bulkApprove(ids);
  };

  const review = async (it, approved) => {
    setBusy(`${it.id}-${approved}`);
    try {
      await api.post(`/admin/punch-audit/${it.kind}/${it.id}`, { approved });
      toast.success(approved ? "Ponto aprovado" : "Ponto reprovado");
      await refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      <PeriodSelector start={start} end={end} setStart={setStart} setEnd={setEnd} testPrefix="ponto-aud-period" />
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 p-3">
        <span className="text-sm text-slate-300" data-testid="punch-selected-count">{selected.size} selecionada(s)</span>
        <Button size="sm" className="h-8 bg-emerald-600 text-white hover:bg-emerald-700" disabled={bulkBusy || selected.size === 0} onClick={approveSelected} data-testid="punch-approve-selected">
          <Check className="h-4 w-4" /> Aprovar selecionadas
        </Button>
        <Button size="sm" variant="outline" className="h-8 border-emerald-700/60 text-emerald-300 hover:bg-emerald-500/10" disabled={bulkBusy || pendingVisible.length === 0} onClick={approveAllPending} data-testid="punch-approve-all">
          <CheckCheck className="h-4 w-4" /> Aprovar todas pendentes ({pendingVisible.length})
        </Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
        <table className="w-full text-sm" data-testid="punch-audit-table">
          <thead>
            <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-semibold">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} data-testid="punch-select-all" className="h-4 w-4 cursor-pointer accent-orange-500" />
              </th>
              <th className="px-4 py-3 font-semibold">Colaborador</th><th className="px-4 py-3 font-semibold">Categoria</th>
              <th className="px-4 py-3 font-semibold">Data</th><th className="px-4 py-3 font-semibold">Origem</th>
              <th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold">Tipo</th><th className="px-4 py-3 text-right font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {items.length === 0 && <tr><td colSpan={8}><Empty label="Nenhum ponto para auditar" /></td></tr>}
            {items.map((it) => (
              <tr key={`${it.kind}-${it.id}`} className="hover:bg-slate-800/40" data-testid={`punch-row-${it.id}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelect(it.id)} data-testid={`punch-select-${it.id}`} className="h-4 w-4 cursor-pointer accent-orange-500" />
                </td>
                <td className="px-4 py-3 font-medium text-slate-100">{it.person_name}</td>
                <td className="px-4 py-3 capitalize text-slate-400">{it.category}</td>
                <td className="px-4 py-3 text-slate-300">{fmtDate(it.date)} · {timeOf(it.timestamp)}</td>
                <td className="px-4 py-3 text-slate-400" data-testid={`punch-origin-${it.id}`}>{it.is_manual || it.manual ? "Manual" : "App"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${it.review_status === "approved" ? "bg-emerald-500/15 text-emerald-300" : it.review_status === "reproved" ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-300"}`}>
                    {it.review_status === "approved" ? "Aprovado" : it.review_status === "reproved" ? "Reprovado" : "Pendente"}
                  </span>
                </td>
                <td className="px-4 py-3" data-testid={`punch-type-${it.id}`}>
                  {(() => { const t = String(it.punch_type || it.type || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase(); return t ? <span className={`rounded px-2 py-0.5 text-xs font-semibold ${t === "entrada" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>{t === "entrada" ? "Entrada" : t === "saida" ? "Saída" : (it.punch_type || it.type)}</span> : <span className="text-slate-600">—</span>; })()}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    {it.photo && !(it.is_manual || it.manual) && <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-300" onClick={() => setPhoto(it)} data-testid={`punch-photo-${it.id}`}><Images className="h-4 w-4" /></Button>}
                    <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700" disabled={busy === `${it.id}-true`} onClick={() => review(it, true)} data-testid={`punch-approve-${it.id}`}><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" disabled={busy === `${it.id}-false`} onClick={() => review(it, false)} data-testid={`punch-reject-${it.id}`}><X className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {photo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setPhoto(null)}>
          <AuthImage path={photo.photo} className="max-h-[80vh] w-auto rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}

export default function Ponto() {
  // Mantém a aba aberta quando a página recarrega ao trocar a base no topo.
  const [tab, setTab] = useState(() => sessionStorage.getItem("ponto_tab") || "registro");
  const changeTab = (v) => { setTab(v); sessionStorage.setItem("ponto_tab", v); };
  return (
    <div data-testid="ponto">
      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="bg-slate-900">
          <TabsTrigger value="registro" data-testid="ponto-tab-registro"><Clock className="mr-1.5 h-4 w-4" /> Registro manual</TabsTrigger>
          <TabsTrigger value="auditoria" data-testid="ponto-tab-auditoria"><ClipboardCheck className="mr-1.5 h-4 w-4" /> Auditoria de ponto</TabsTrigger>
        </TabsList>
        <TabsContent value="registro" className="mt-4"><ManualPunch /></TabsContent>
        <TabsContent value="auditoria" className="mt-4"><PunchAudit /></TabsContent>
      </Tabs>
    </div>
  );
}
